/**
 * AdminModeIndicator Component
 * Displays admin mode status with countdown timer and controls
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import {
  requestAdminMode,
  disposeAdminMode,
} from '../../services/adminModeService';
import './AdminModeIndicator.css';

export default function AdminModeIndicator() {
  const { t } = useTranslation();
  const { currentUser, adminMode, refreshAdminMode } = useAuth();
  const [requesting, setRequesting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Format time remaining as MM:SS
  const formatTimeRemaining = (ms: number | null): string => {
    if (!ms) return '--:--';

    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return `${minutes.toString().padStart(2, '0')}:${seconds
      .toString()
      .padStart(2, '0')}`;
  };

  // Check if we're in warning period (< 2 minutes)
  const isWarning =
    adminMode.timeRemaining !== null && adminMode.timeRemaining < 120000; // 2 minutes

  // Handle refresh admin mode
  const handleRefreshAdminMode = async () => {
    setRequesting(true);
    setError(null);

    try {
      // Request the same type of admin mode
      if (!adminMode.type) {
        setError('Cannot refresh - admin mode type unknown');
        return;
      }

      const adminType = adminMode.type;
      const request: { adminType: 'system' | 'org'; orgId?: string } = {
        adminType,
      };

      if (adminType === 'org' && adminMode.orgId) {
        request.orgId = adminMode.orgId;
      }

      console.log('Admin mode refresh requested:', {
        userId: currentUser?.uid,
        email: currentUser?.email,
        displayName: currentUser?.displayName,
        adminType,
        orgId: adminMode.orgId
      });

      await requestAdminMode(request);

      // Refresh token to get new claims
      await refreshAdminMode();

      console.log('Admin mode refreshed:', {
        userId: currentUser?.uid,
        email: currentUser?.email,
        displayName: currentUser?.displayName,
        adminType,
        orgId: adminMode.orgId
      });

      // Refresh page to apply updated status
      window.location.reload();
    } catch (err) {
      console.error('Error refreshing admin mode:', err);
      setError(
        err instanceof Error ? err.message : 'Failed to refresh admin mode'
      );
    } finally {
      setRequesting(false);
    }
  };

  // Handle dispose admin mode
  const handleDisposeAdminMode = async () => {
    setRequesting(true);
    setError(null);

    try {
      console.log('Admin mode dispose requested:', {
        userId: currentUser?.uid,
        email: currentUser?.email,
        displayName: currentUser?.displayName,
        adminType: adminMode.type,
        orgId: adminMode.orgId
      });

      await disposeAdminMode();

      // Refresh token to clear claims
      await refreshAdminMode();

      console.log('Admin mode disposed:', {
        userId: currentUser?.uid,
        email: currentUser?.email,
        displayName: currentUser?.displayName,
        adminType: adminMode.type,
        orgId: adminMode.orgId
      });

      // Refresh page to apply status change
      window.location.reload();
    } catch (err) {
      console.error('Error disposing admin mode:', err);
      setError(
        err instanceof Error ? err.message : 'Failed to dispose admin mode'
      );
    } finally {
      setRequesting(false);
    }
  };

  // Don't show anything if not active
  if (!adminMode.isActive) {
    return null;
  }

  return (
    <div className={`admin-mode-indicator ${isWarning ? 'warning' : ''}`}>
      <div className="admin-mode-content">
        <div className="admin-mode-info">
          <span className="admin-mode-label">
            {t('adminMode.active')}
            {adminMode.type === 'org' && adminMode.orgId && (
              <span className="admin-mode-org"> ({adminMode.orgId})</span>
            )}
          </span>
          <span
            className={`admin-mode-timer ${isWarning ? 'warning' : ''}`}
            title={t('adminMode.timeRemaining')}
          >
            {formatTimeRemaining(adminMode.timeRemaining)}
          </span>
        </div>

        <div className="admin-mode-actions">
          <button
            className={`admin-mode-btn refresh ${isWarning ? 'highlight' : ''}`}
            onClick={handleRefreshAdminMode}
            disabled={requesting}
            title={t('adminMode.refresh')}
            aria-label={t('adminMode.refresh')}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2" />
            </svg>
          </button>

          <button
            className="admin-mode-btn dispose"
            onClick={handleDisposeAdminMode}
            disabled={requesting}
            title={t('adminMode.dispose')}
            aria-label={t('adminMode.dispose')}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>

      {error && <div className="admin-mode-error">{error}</div>}
    </div>
  );
}
